import { useState, useEffect, useCallback, useMemo, useRef } from 'react';

const BASE_URL = '/api';
function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('ozon_token') || ''}` };
}
function daysSince(dateStr) {
  if (!dateStr) return null;
  return Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
}
function formatDateRange(begin, end) {
  if (!begin && !end) return '—';
  const fmt = d => new Date(d).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' });
  if (begin && end) return `${fmt(begin)} – ${fmt(end)}`;
  return fmt(begin || end);
}
function fmtTime(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function fillTemplate(order, tpl, categoryKey) {
  if (!tpl?.template) return null;
  const deliveryRange = formatDateRange(order.delivery_date_begin, order.delivery_date_end);
  const productNames = (order.products || []).map(p => p.name).join(', ');
  return tpl.template
    .replace(/{musteri_adi}/g, order.customer_name || '')
    .replace(/{siparis_no}/g, order.posting_number)
    .replace(/{teslimat_tarihi}/g, deliveryRange)
    .replace(/{nokta_adi}/g, order.pickup_address || order.delivery_method_name || '')
    .replace(/{sehir}/g, order.city || '')
    .replace(/{urun}/g, productNames);
}

// ── Mesaj Gönder Modal ───────────────────────────────────────────────────────
function SendMessageModal({ order, templates, categoryKey, onClose, onSent }) {
  const tpl = templates.find(t => t.key === categoryKey);
  const [text, setText] = useState(() => fillTemplate(order, tpl, categoryKey) || '');
  const [loading, setLoading] = useState(false);
  const [dryResult, setDryResult] = useState(null);
  const [error, setError] = useState(null);

  async function handleDryRun() {
    if (!text.trim()) { setError('Mesaj metni boş olamaz.'); return; }
    setLoading(true); setDryResult(null); setError(null);
    try {
      const r = await fetch(`${BASE_URL}/delivery-tracking/send-ozon-message`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ posting_number: order.posting_number, message: text, dry_run: true }),
      });
      const d = await r.json();
      if (!r.ok) setError(d.error || 'Hata');
      else setDryResult(d);
    } catch (e) { setError(e.message); }
    setLoading(false);
  }

  async function handleRealSend() {
    if (!text.trim()) { setError('Mesaj metni boş olamaz.'); return; }
    setLoading(true); setError(null);
    try {
      const r = await fetch(`${BASE_URL}/delivery-tracking/send-ozon-message`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ posting_number: order.posting_number, message: text, dry_run: false, category_key: categoryKey }),
      });
      const d = await r.json();
      if (!r.ok) { setError(d.error || 'Gönderilemedi'); setLoading(false); return; }
      onSent(order.posting_number, categoryKey);
      onClose();
    } catch (e) { setError(e.message); }
    setLoading(false);
  }

  async function handleMarkSent() {
    setLoading(true);
    try {
      await fetch(`${BASE_URL}/delivery-tracking/mark-sent`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ posting_number: order.posting_number, substatus: categoryKey, note: text }),
      });
      onSent(order.posting_number, categoryKey);
      onClose();
    } catch {}
    setLoading(false);
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <div className="font-semibold text-gray-900">Mesaj Gönder</div>
            <div className="text-xs text-gray-500 font-mono mt-0.5">{order.posting_number}</div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
        </div>
        <div className="px-5 py-4">
          <div className="text-xs text-gray-500 mb-1.5 font-medium">Müşteri: {order.customer_name} · {order.city}</div>
          <div className="text-xs text-gray-400 mb-3">
            {(order.products || []).map((p, i) => (
              <span key={i} className="block truncate">×{p.quantity} {p.name}</span>
            ))}
          </div>
          <label className="text-xs font-medium text-gray-600 block mb-1">Mesaj metni</label>
          <textarea value={text} onChange={e => setText(e.target.value)} rows={7}
            className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-800 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Şablon tanımlanmamış. Buraya mesaj metnini yazın..." />
          {!tpl?.template && (
            <p className="text-xs text-amber-600 mt-1">Bu kategori için şablon tanımlı değil. Mesaj Şablonları sekmesinden ekleyebilirsiniz.</p>
          )}
        </div>
        {dryResult && (
          <div className="mx-5 mb-3 bg-green-50 border border-green-200 rounded-xl p-3 text-sm">
            <div className="font-semibold text-green-800 mb-1">✓ Bağlantı başarılı — mesaj gönderilebilir</div>
            <div className="text-xs text-green-700">Chat ID: <code className="font-mono">{dryResult.chat_id}</code></div>
            <div className="text-xs text-green-600 mt-1">Mesaj gerçekten gönderilmedi.</div>
          </div>
        )}
        {error && <div className="mx-5 mb-3 bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">✗ {error}</div>}
        <div className="px-5 py-3 border-t border-gray-100 flex flex-wrap justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">İptal</button>
          <button onClick={handleDryRun} disabled={loading}
            className="px-4 py-2 text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg disabled:opacity-50">
            {loading && !dryResult ? '⏳ Test...' : '🧪 Dry Run'}
          </button>
          {dryResult && (
            <button onClick={handleRealSend} disabled={loading}
              className="px-5 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {loading ? 'Gönderiliyor...' : '🚀 Gerçek Gönder'}
            </button>
          )}
          <button onClick={handleMarkSent} disabled={loading}
            className="px-4 py-2 text-sm font-medium bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50">
            ✓ Manuel İşaretle
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Sipariş Kartı ────────────────────────────────────────────────────────────
function OrderCard({ order, templates, categoryKey, onSent, selected, onSelect }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [undoing, setUndoing] = useState(false);
  const days = daysSince(order.delivering_date);
  const sent = !!order.message_sent_at;

  async function handleUndo() {
    setUndoing(true);
    try {
      await fetch(`${BASE_URL}/delivery-tracking/mark-sent/${order.posting_number}/${categoryKey}`, {
        method: 'DELETE', headers: authHeaders(),
      });
      onSent(order.posting_number, categoryKey, true);
    } catch {}
    setUndoing(false);
  }

  return (
    <>
      <div className={`bg-white border rounded-xl p-4 transition-all ${
        selected ? 'border-blue-400 ring-2 ring-blue-200' :
        sent ? 'border-green-200 opacity-75' : 'border-gray-200 hover:border-blue-300 hover:shadow-sm'
      }`}>
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-start gap-2 min-w-0">
            {/* Checkbox — sadece gönderilmemiş siparişlerde */}
            {!sent && (
              <input
                type="checkbox"
                checked={!!selected}
                onChange={() => onSelect(order.posting_number)}
                className="mt-0.5 shrink-0 w-4 h-4 rounded border-gray-300 text-blue-600 cursor-pointer"
                onClick={e => e.stopPropagation()}
              />
            )}
            <div className="min-w-0">
              <div className="font-mono text-sm font-semibold text-gray-800 truncate">{order.posting_number}</div>
              <div className="text-xs text-gray-500 mt-0.5 truncate">{order.customer_name || '—'}</div>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1 shrink-0">
            {order.city && <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">📍 {order.city}</span>}
            {days !== null && (
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                days >= 10 ? 'bg-red-100 text-red-700' : days >= 5 ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'
              }`}>{days}g</span>
            )}
          </div>
        </div>
        <div className="mb-3 space-y-0.5">
          {(order.products || []).map((p, i) => (
            <div key={i} className="flex items-start gap-1.5">
              <span className="text-xs text-gray-400 mt-0.5 shrink-0">×{p.quantity}</span>
              <span className="text-xs text-gray-600 leading-snug line-clamp-2">{p.name}</span>
            </div>
          ))}
        </div>
        <div className="border-t border-gray-100 pt-2 space-y-1 mb-3">
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-400">Tahmini Teslimat</span>
            <span className="font-medium text-gray-700">{formatDateRange(order.delivery_date_begin, order.delivery_date_end)}</span>
          </div>
          {(order.pickup_address || order.delivery_method_name) && (
            <div className="flex items-start justify-between text-xs gap-2">
              <span className="text-gray-400 shrink-0">Teslimat Noktası</span>
              <span className="text-gray-700 text-right leading-snug line-clamp-2">{order.pickup_address || order.delivery_method_name}</span>
            </div>
          )}
        </div>
        {sent ? (
          <div className="flex items-center justify-between bg-green-50 rounded-lg px-3 py-2">
            <div className="flex items-center gap-1.5 text-xs text-green-700">
              <span>✓</span><span className="font-medium">Mesaj gönderildi</span>
              <span className="text-green-500">· {fmtTime(order.message_sent_at)}</span>
            </div>
            <button onClick={handleUndo} disabled={undoing}
              className="text-xs text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50">
              {undoing ? '...' : 'Geri Al'}
            </button>
          </div>
        ) : (
          <button onClick={() => setModalOpen(true)}
            className="w-full flex items-center justify-center gap-2 py-2 text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors">
            ✉️ Mesaj Gönder
          </button>
        )}
      </div>
      {modalOpen && (
        <SendMessageModal order={order} templates={templates} categoryKey={categoryKey}
          onClose={() => setModalOpen(false)}
          onSent={(pn, ck) => { setModalOpen(false); onSent(pn, ck); }} />
      )}
    </>
  );
}

// ── Toplu Gönderim Paneli ────────────────────────────────────────────────────
function BulkSendPanel({ selected, orders, templates, categoryKey, onSent, onClear }) {
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState(null); // { current, total, results[], currentPn }
  const [done, setDone] = useState(false);

  const tpl = templates.find(t => t.key === categoryKey);
  const hasTemplate = !!tpl?.template;

  const pollRef = useRef(null);
  const prevResultsLen = useRef(0);

  async function handleSend() {
    const posting_numbers = [...selected].filter(pn => {
      const o = orders.find(o => o.posting_number === pn);
      return o && !o.message_sent_at;
    });
    if (!posting_numbers.length) return;

    setSending(true);
    setDone(false);
    setProgress({ current: 0, total: posting_numbers.length, results: [], currentPn: null });
    prevResultsLen.current = 0;

    try {
      const r = await fetch(`${BASE_URL}/delivery-tracking/bulk-send`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ posting_numbers, category_key: categoryKey }),
      });
      const d = await r.json();
      if (!r.ok || !d.job_id) {
        setProgress(prev => ({ ...prev, results: [{ pn: '—', ok: false, error: d.error || 'Job başlatılamadı' }] }));
        setSending(false); setDone(true); return;
      }

      // Polling — 2s aralıklarla job durumunu sorgula
      pollRef.current = setInterval(async () => {
        try {
          const pr = await fetch(`${BASE_URL}/delivery-tracking/bulk-job/${d.job_id}`, { headers: authHeaders() });
          const job = await pr.json();
          if (!pr.ok) return;

          // Yeni gelen result'ları işle (yerel state'e yansıt)
          const newResults = job.results.slice(prevResultsLen.current);
          prevResultsLen.current = job.results.length;
          newResults.forEach(res => { if (res.ok) onSent(res.pn, categoryKey); });

          setProgress({
            current: job.current,
            total: job.total,
            currentPn: job.currentPn,
            results: job.results,
          });

          if (job.done) {
            clearInterval(pollRef.current);
            setSending(false);
            setDone(true);
          }
        } catch {}
      }, 2000);

    } catch (e) {
      setProgress(prev => ({ ...prev, results: [{ pn: '—', ok: false, error: e.message }] }));
      setSending(false); setDone(true);
    }
  }

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  if (!progress) {
    return (
      <div className="mb-4 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-blue-800">
          <input type="checkbox" checked readOnly className="w-4 h-4 rounded" />
          <span className="font-medium">{selected.size} sipariş seçildi</span>
          {!hasTemplate && <span className="text-amber-600 text-xs">⚠️ Bu kategori için şablon tanımlı değil</span>}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onClear} className="text-xs text-gray-500 hover:text-gray-700 px-3 py-1.5 rounded-lg hover:bg-white transition-colors">
            Seçimi Kaldır
          </button>
          <button
            onClick={handleSend}
            disabled={!hasTemplate}
            className="flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            🚀 Toplu Gönder ({selected.size})
          </button>
        </div>
      </div>
    );
  }

  const successCount = progress.results.filter(r => r.ok).length;
  const failCount    = progress.results.filter(r => !r.ok).length;
  const pct = Math.round((progress.current / progress.total) * 100);

  return (
    <div className="mb-4 bg-white border border-blue-200 rounded-xl overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <div className="text-sm font-medium text-gray-800">
          {sending ? `Gönderiliyor... ${progress.current}/${progress.total}` : `Tamamlandı — ${successCount} başarılı, ${failCount} hatalı`}
        </div>
        {done && (
          <button onClick={() => { setProgress(null); setDone(false); onClear(); }}
            className="text-xs text-gray-500 hover:text-gray-700 px-3 py-1 rounded-lg hover:bg-gray-100 transition-colors">
            Kapat
          </button>
        )}
      </div>
      {/* Progress bar */}
      <div className="h-1.5 bg-gray-100">
        <div className="h-full bg-blue-500 transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
      {/* Sonuçlar */}
      <div className="px-4 py-2 max-h-40 overflow-y-auto space-y-1">
        {progress.results.map((r, i) => (
          <div key={i} className="flex items-center gap-2 text-xs">
            <span className={r.ok ? 'text-green-600' : 'text-red-500'}>{r.ok ? '✓' : '✗'}</span>
            <span className="font-mono text-gray-600">{r.pn}</span>
            {!r.ok && <span className="text-red-400">{r.error}</span>}
          </div>
        ))}
        {sending && progress.currentPn && (
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <span className="inline-block animate-spin">↻</span>
            <span className="font-mono">{progress.currentPn}</span>
            <span>gönderiliyor...</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Sipariş Listesi ──────────────────────────────────────────────────────────
function OrderList({ orders, loading, templates, categoryKey, onSent }) {
  const [search, setSearch] = useState('');
  const [msgFilter, setMsgFilter] = useState('all');
  const [sortBy, setSortBy] = useState('days_desc');
  const [selected, setSelected] = useState(new Set());

  const filtered = useMemo(() => {
    let list = [...orders];
    if (msgFilter === 'unsent') list = list.filter(o => !o.message_sent_at);
    if (msgFilter === 'sent')   list = list.filter(o =>  o.message_sent_at);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(o =>
        o.posting_number?.toLowerCase().includes(q) ||
        o.customer_name?.toLowerCase().includes(q) ||
        (o.products || []).some(p => p.name?.toLowerCase().includes(q)) ||
        o.city?.toLowerCase().includes(q)
      );
    }
    list.sort((a, b) => {
      const da = daysSince(a.delivering_date) ?? 0;
      const db_ = daysSince(b.delivering_date) ?? 0;
      if (sortBy === 'days_desc') return db_ - da;
      if (sortBy === 'days_asc')  return da - db_;
      if (sortBy === 'date_asc')  return new Date(a.delivery_date_begin || 0) - new Date(b.delivery_date_begin || 0);
      return 0;
    });
    return list;
  }, [orders, search, msgFilter, sortBy]);

  const unsentCount   = orders.filter(o => !o.message_sent_at).length;
  const sentCount     = orders.filter(o =>  o.message_sent_at).length;
  const unsentVisible = filtered.filter(o => !o.message_sent_at);
  const allUnsentSelected = unsentVisible.length > 0 && unsentVisible.every(o => selected.has(o.posting_number));

  function toggleSelect(pn) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(pn)) next.delete(pn); else next.add(pn);
      return next;
    });
  }

  function toggleSelectAll() {
    if (allUnsentSelected) {
      setSelected(prev => {
        const next = new Set(prev);
        unsentVisible.forEach(o => next.delete(o.posting_number));
        return next;
      });
    } else {
      setSelected(prev => {
        const next = new Set(prev);
        unsentVisible.forEach(o => next.add(o.posting_number));
        return next;
      });
    }
  }

  function clearSelection() {
    setSelected(new Set());
  }

  const activeSelected = new Set([...selected].filter(pn => orders.find(o => o.posting_number === pn && !o.message_sent_at)));

  if (loading) return (
    <div className="space-y-3 max-w-2xl">
      {[1,2,3].map(i => (
        <div key={i} className="bg-white border border-gray-100 rounded-xl p-4 animate-pulse">
          <div className="h-4 bg-gray-100 rounded w-2/3 mb-2" />
          <div className="h-3 bg-gray-100 rounded w-1/2 mb-4" />
          <div className="h-3 bg-gray-100 rounded w-full mb-1" />
          <div className="h-3 bg-gray-100 rounded w-3/4" />
        </div>
      ))}
    </div>
  );

  return (
    <div className="max-w-2xl">
      {/* Toplu seçim/gönderim paneli */}
      {activeSelected.size > 0 && (
        <BulkSendPanel
          selected={activeSelected}
          orders={orders}
          templates={templates}
          categoryKey={categoryKey}
          onSent={onSent}
          onClear={clearSelection}
        />
      )}

      {/* Filtre satırı */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-48">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🔍</span>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Sipariş no, müşteri, ürün, şehir..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
          {search && <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">✕</button>}
        </div>
        <div className="flex bg-gray-100 rounded-lg p-0.5 text-xs font-medium">
          {[['all',`Tümü (${orders.length})`],['unsent',`Gönderilmedi (${unsentCount})`],['sent',`Gönderildi (${sentCount})`]].map(([k,l]) => (
            <button key={k} onClick={() => setMsgFilter(k)}
              className={`px-3 py-1.5 rounded-md transition-colors ${msgFilter === k ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
              {l}
            </button>
          ))}
        </div>
        <select value={sortBy} onChange={e => setSortBy(e.target.value)}
          className="px-3 py-2 text-xs border border-gray-200 rounded-lg text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
          <option value="days_desc">En Uzun</option>
          <option value="days_asc">En Kısa</option>
          <option value="date_asc">Teslimat Tarihi</option>
        </select>
        {/* Tümünü seç butonu — sadece gönderilmemiş varsa */}
        {unsentVisible.length > 0 && (
          <button onClick={toggleSelectAll}
            className="px-3 py-2 text-xs border border-gray-200 rounded-lg text-gray-600 bg-white hover:bg-gray-50 transition-colors whitespace-nowrap">
            {allUnsentSelected ? '☐ Seçimi Kaldır' : `☑ Tümünü Seç (${unsentVisible.length})`}
          </button>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white border border-dashed border-gray-200 rounded-xl p-12 text-center">
          <div className="text-3xl mb-2">📭</div>
          <div className="text-gray-400 text-sm">{orders.length === 0 ? 'Bu kategoride sipariş yok' : 'Filtreyle eşleşen sipariş bulunamadı'}</div>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(o => (
            <OrderCard
              key={o.posting_number}
              order={o}
              templates={templates}
              categoryKey={categoryKey}
              onSent={onSent}
              selected={selected.has(o.posting_number)}
              onSelect={toggleSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Şablonlar Sekmesi ────────────────────────────────────────────────────────
function TemplatesTab({ onTemplateSaved }) {
  const [templates, setTemplates] = useState([]);
  const [saving, setSaving] = useState({});
  const [saved, setSaved]   = useState({});

  useEffect(() => {
    fetch(`${BASE_URL}/delivery-tracking/templates`, { headers: authHeaders() })
      .then(r => r.json()).then(data => Array.isArray(data) && setTemplates(data)).catch(() => {});
  }, []);

  function handleChange(key, value) {
    setTemplates(ts => ts.map(t => t.key === key ? { ...t, template: value } : t));
  }

  async function handleSave(key, template) {
    setSaving(s => ({ ...s, [key]: true }));
    try {
      await fetch(`${BASE_URL}/delivery-tracking/templates/${key}`, {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ template }),
      });
      setSaved(s => ({ ...s, [key]: true }));
      setTimeout(() => setSaved(s => ({ ...s, [key]: false })), 2000);
      if (onTemplateSaved) onTemplateSaved();
    } catch {}
    setSaving(s => ({ ...s, [key]: false }));
  }

  const VARS = ['{musteri_adi}', '{siparis_no}', '{teslimat_tarihi}', '{nokta_adi}', '{sehir}', '{urun}'];
  const CATEGORY_LABELS = {
    new_order: { icon: '🆕', color: 'text-blue-700' },
    shipped:   { icon: '🚚', color: 'text-orange-700' },
    at_point:  { icon: '📍', color: 'text-purple-700' },
    delivered: { icon: '✅', color: 'text-green-700' },
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800">
        <div className="font-semibold mb-2">Kullanılabilir değişkenler</div>
        <div className="flex flex-wrap gap-2">
          {VARS.map(v => <code key={v} className="bg-white border border-blue-200 px-2 py-0.5 rounded text-xs font-mono">{v}</code>)}
        </div>
        <p className="text-xs text-blue-600 mt-2">Mesaj gönderirken bu değişkenler sipariş bilgileriyle otomatik doldurulur.</p>
      </div>
      {templates.map(t => {
        const cat = CATEGORY_LABELS[t.key] || {};
        return (
          <div key={t.key} className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="mb-3 flex items-center gap-2">
              <span className="text-lg">{cat.icon}</span>
              <div>
                <div className={`font-semibold ${cat.color || 'text-gray-800'}`}>{t.label}</div>
                <code className="text-xs text-gray-400 font-mono">{t.key}</code>
              </div>
            </div>
            <textarea value={t.template || ''} onChange={e => handleChange(t.key, e.target.value)}
              placeholder={`${t.label} için mesaj şablonu yazın...`} rows={6}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-800 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <div className="flex justify-end mt-2">
              <button onClick={() => handleSave(t.key, t.template)} disabled={saving[t.key]}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  saved[t.key] ? 'bg-green-500 text-white' : 'bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50'
                }`}>
                {saved[t.key] ? '✓ Kaydedildi' : saving[t.key] ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Ana Bileşen ──────────────────────────────────────────────────────────────
const TABS = [
  { key: 'new_order', label: 'Yeni Sipariş',         icon: '🆕' },
  { key: 'shipped',   label: 'Kargoya Verildi',       icon: '🚚' },
  { key: 'at_point',  label: 'Teslimat Noktasında',   icon: '📍' },
  { key: 'delivered', label: 'Teslim Edildi',          icon: '✅' },
  { key: 'templates', label: 'Mesaj Şablonları',       icon: '✉️' },
];

export default function DeliveryTracking() {
  const [tab, setTab] = useState('at_point');
  const [data, setData] = useState({ new_order: [], shipped: [], at_point: [], delivered: [] });
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [error, setError] = useState(null);
  const [templates, setTemplates] = useState([]);

  const loadTemplates = useCallback(async () => {
    try {
      const r = await fetch(`${BASE_URL}/delivery-tracking/templates`, { headers: authHeaders() });
      const t = await r.json();
      if (Array.isArray(t)) setTemplates(t);
    } catch {}
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [dataRes, tplRes] = await Promise.all([
        fetch(`${BASE_URL}/delivery-tracking?v=2`, { headers: authHeaders() }),
        fetch(`${BASE_URL}/delivery-tracking/templates`, { headers: authHeaders() }),
      ]);
      if (!dataRes.ok) throw new Error(`HTTP ${dataRes.status}`);
      const [d, t] = await Promise.all([dataRes.json(), tplRes.json()]);
      setData(d);
      if (Array.isArray(t)) setTemplates(t);
      setLastUpdated(new Date());
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  function handleSent(postingNumber, categoryKey, undo = false) {
    setData(prev => {
      function update(list) {
        return list.map(o => o.posting_number !== postingNumber ? o : {
          ...o,
          message_sent_at: undo ? null : new Date().toISOString(),
          message_sent_by: undo ? null : 'manual',
        });
      }
      return {
        new_order: update(prev.new_order || []),
        shipped:   update(prev.shipped   || []),
        at_point:  update(prev.at_point  || []),
        delivered: update(prev.delivered || []),
      };
    });
  }

  const counts = {
    new_order: data.new_order?.length ?? 0,
    shipped:   data.shipped?.length   ?? 0,
    at_point:  data.at_point?.length  ?? 0,
    delivered: data.delivered?.length ?? 0,
  };
  const unsent = {
    new_order: (data.new_order || []).filter(o => !o.message_sent_at).length,
    shipped:   (data.shipped   || []).filter(o => !o.message_sent_at).length,
    at_point:  (data.at_point  || []).filter(o => !o.message_sent_at).length,
    delivered: (data.delivered || []).filter(o => !o.message_sent_at).length,
  };

  return (
    <div className="min-h-full flex flex-col bg-gray-50">
      <div className="px-6 pt-6 pb-0 bg-white border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Teslimat Takibi</h1>
            {lastUpdated && (
              <p className="text-xs text-gray-400 mt-0.5">Son güncelleme: {lastUpdated.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {!loading && (
              <div className="flex gap-2 text-xs">
                {[
                  { key: 'new_order', icon: '🆕', color: 'bg-blue-50 text-blue-700' },
                  { key: 'shipped',   icon: '🚚', color: 'bg-orange-50 text-orange-700' },
                  { key: 'at_point',  icon: '📍', color: 'bg-purple-50 text-purple-700' },
                  { key: 'delivered', icon: '✅', color: 'bg-green-50 text-green-700' },
                ].map(c => (
                  <span key={c.key} className={`${c.color} px-2.5 py-1.5 rounded-lg font-medium`}>
                    {c.icon} {counts[c.key]}
                    {unsent[c.key] > 0 && <span className="ml-1 opacity-70">({unsent[c.key]})</span>}
                  </span>
                ))}
              </div>
            )}
            <button onClick={load} disabled={loading}
              className="px-3 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50">
              <span className={loading ? 'inline-block animate-spin' : ''}>↻</span> Yenile
            </button>
          </div>
        </div>

        {error && <div className="mb-3 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">Hata: {error}</div>}

        <div className="flex gap-1">
          {TABS.map(t => {
            const badge = unsent[t.key] ?? 0;
            return (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium rounded-t-lg border-b-2 -mb-px transition-colors ${
                  tab === t.key ? 'border-blue-600 text-blue-700 bg-blue-50' : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}>
                <span>{t.icon}</span>{t.label}
                {badge > 0 && (
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none ${tab === t.key ? 'bg-blue-600 text-white' : 'bg-red-500 text-white'}`}>
                    {badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex-1 p-6">
        {tab === 'templates' ? <TemplatesTab onTemplateSaved={loadTemplates} /> : (
          <OrderList
            orders={data[tab] || []}
            loading={loading}
            templates={templates}
            categoryKey={tab}
            onSent={handleSent}
          />
        )}
      </div>
    </div>
  );
}
